// src/utils/waitlist.ts
//
// Čekací listina závodu – kolekce waitlist/{raceId__uid}
// { raceId, userId, createdAt } (varianta C rozhodnutá majitelem 3. 10. 2026:
// upozornění v aplikaci „Uvolnilo se místo"; od 5. 10. 2026 navíc e-mail –
// posílá ho server při uvolněném místě všem na listině a po konci závodu
// listinu smaže, cloud-functions/payments/waitlist.py).
//
// - Zapsat se jde jen na plný závod a jen bez přihlášky (firestore.rules).
// - Pořadí = podle createdAt; je informativní: když se místo uvolní, vidí
//   upozornění všichni na listině a přihlásí se, kdo dřív přijde (přihláška
//   záznam na listině v téže transakci smaže – raceStats.createRegistrationCounted).
// - Dokument nemá jména ani e-maily (jen uid) – pořadí si spočítá každý
//   přihlášený; jména vidí správce v Přihláškách (čte profily).

import {
  collection,
  deleteDoc,
  doc,
  getDocFromServer,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import { db } from "../firebase";
import { toDateSafe } from "./pricing";

export const WAITLIST_COLLECTION = "waitlist";

export const waitlistId = (raceId: string, uid: string): string => `${raceId}__${uid}`;

export interface WaitlistEntry {
  id: string;
  raceId: string;
  userId: string;
  /** Čas zápisu; null = ještě nepotvrzený serverem (lokální zápis). */
  createdAt: Date | null;
}

export function parseWaitlistEntry(id: string, data: DocumentData): WaitlistEntry {
  return {
    id,
    raceId: typeof data.raceId === "string" ? data.raceId : "",
    userId: typeof data.userId === "string" ? data.userId : "",
    createdAt: toDateSafe(data.createdAt),
  };
}

/** Řazení podle času zápisu (nepotvrzený zápis na konec), pak podle id. */
export function sortWaitlist(entries: ReadonlyArray<WaitlistEntry>): WaitlistEntry[] {
  return [...entries].sort((a, b) => {
    const ta = a.createdAt ? a.createdAt.getTime() : Number.POSITIVE_INFINITY;
    const tb = b.createdAt ? b.createdAt.getTime() : Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** Čekací listina závodu seřazená podle pořadí. */
export async function loadRaceWaitlist(raceId: string): Promise<WaitlistEntry[]> {
  const snap = await getDocs(
    query(collection(db, WAITLIST_COLLECTION), where("raceId", "==", raceId))
  );
  return sortWaitlist(snap.docs.map((d) => parseWaitlistEntry(d.id, d.data())));
}

/** Závody, na jejichž čekací listině uživatel je. */
export async function loadMyWaitlist(uid: string): Promise<WaitlistEntry[]> {
  const snap = await getDocs(
    query(collection(db, WAITLIST_COLLECTION), where("userId", "==", uid))
  );
  return snap.docs.map((d) => parseWaitlistEntry(d.id, d.data()));
}

/** Pořadí uživatele na listině (1 = první) a celkový počet; null = na listině není. */
export function waitlistPosition(
  entries: ReadonlyArray<WaitlistEntry>,
  uid: string
): { position: number; total: number } | null {
  const sorted = sortWaitlist(entries);
  const index = sorted.findIndex((e) => e.userId === uid);
  return index < 0 ? null : { position: index + 1, total: sorted.length };
}

/** Zápis na čekací listinu (pravidla: jen plný závod a jen bez přihlášky). */
export async function joinWaitlist(raceId: string, uid: string): Promise<void> {
  await setDoc(doc(db, WAITLIST_COLLECTION, waitlistId(raceId, uid)), {
    raceId,
    userId: uid,
    createdAt: serverTimestamp(),
  });
}

const isPermissionDenied = (err: unknown): boolean =>
  typeof err === "object" &&
  err !== null &&
  (err as { code?: unknown }).code === "permission-denied";

/**
 * Odebrání z čekací listiny (vlastník nebo správce). Záznam mohl mezitím
 * zmizet – přihláška v jiném okně ho smazala v téže transakci, nebo střelce
 * odebral správce. Pravidla bez větve pro neexistující záznam (přechodová
 * pravidla f7f6eb0, v produkci do jejich přenasazení) takové smazání
 * odmítnou (permission-denied); proto se po odmítnutí záznam přečte ze
 * serveru, a když už neexistuje, je odchod hotový. Jinak (záznam trvá,
 * ověření se nepovedlo) platí původní chyba.
 */
export async function leaveWaitlist(raceId: string, uid: string): Promise<void> {
  const ref = doc(db, WAITLIST_COLLECTION, waitlistId(raceId, uid));
  try {
    await deleteDoc(ref);
  } catch (err) {
    if (!isPermissionDenied(err)) throw err;
    const gone = await getDocFromServer(ref).then(
      (snap) => !snap.exists(),
      () => false
    );
    if (!gone) throw err;
  }
}
